import {apiHttpClient} from "@/http/httpClient";
import type {PaginationItems} from "@/models/pagination";
import type {TrajectoryListModel, TrajectoryListRequest} from "@/models/trajectory/trajectory-list";
import type {TransportTrajectoryDestinations} from "@/models/trajectory/trajectory-destinations";
import type {AssignmentDestination} from "@/models/assignment/assignment-destination";
import type { MapTrajectoriesRequest } from "@/models/trajectory/map-trajectories-request";
import type { MapTrajectoryListModel } from "@/models/trajectory/map-trajectory-list";
import { TransportActionType } from "@/constants/transport-action-type";
import type { TrajectoryActionStatus } from "@/constants/trajectory-action-status";
import type { TrajectoryEmptyRideStatus } from "@/constants/trajectory-empty-ride-status";
import type { OutSourceStatus } from "@/constants/outsource-status";
import type { TrajectoryTagSummary } from "@/models/trajectory/trajectory-tag";

const ENDPOINT_ASSIGN_TRAJECTORIES = 'Trajectory/AssignMultiple';
const ENDPOINT_UPDATE_ASSIGNMENTS = 'Trajectory/UpdateAssignments';
const ENDPOINT_LIST_TRAJECTORIES = 'Trajectory/List';
const ENDPOINT_GET_DESTINATIONS_BY_ASSIGNMENT_ID = 'Trajectory/GetDestinationsByAssignmentId';
const ENDPOINT_UPDATE_TRAJECTORY_DESTINATIONS = 'Trajectory/UpdateTrajectoryDestinations';
const ENDPOINT_GET_MAP_TRAJECTORIES = 'Trajectory/GetMapTrajectories';
const ENDPOINT_OUTSOURCE_TRAJECTORIES = 'Trajectory/OutsourceTrajectories';
const ENDPOINT_WITHDRAW_OUTSOURCED_TRAJECTORIES = 'Trajectory/WithdrawOutsourcedTrajectories';
const ENDPOINT_SET_TRAJECTORY_STATUS = 'Trajectory/SetStatus';
const ENDPOINT_REVERT_TRAJECTORY_TO_PLANNED = 'Trajectory/RevertToPlanned';
const ENDPOINT_REVERT_EMPTY_RIDE = 'Trajectory/RevertEmptyRide';
const ENDPOINT_SET_OUTSOURCE_STATUS = 'Trajectory/SetOutsourceStatus';
const ENDPOINT_ADD_TRAJECTORY_TAG = 'Trajectory/AddTag';
const ENDPOINT_REMOVE_TRAJECTORY_TAG = 'Trajectory/RemoveTag';
const ENDPOINT_TRAJECTORY_TAG_SUMMARY = 'Trajectory/GetTagSummary';

export const listTrajectories = async (request: TrajectoryListRequest) => {
    return await apiHttpClient().post<PaginationItems<TrajectoryListModel>>(ENDPOINT_LIST_TRAJECTORIES, request)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}
export const assignTrajectories = async (transportId: number, isNewTransport: boolean, trajectoryIds: number[], startDate: number, transportVehicleId: number, assignmentCapacity: number, chauffeurId: string) => {
    return await apiHttpClient().put(ENDPOINT_ASSIGN_TRAJECTORIES, {
        trajectoryIds: trajectoryIds,
        transportId: transportId,
        isNewTransport: isNewTransport,
        startDate: startDate,
        transportVehicleId: transportVehicleId,
        assignmentCapacity: assignmentCapacity,
        chauffeurId: chauffeurId
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateTransportAssignments = async (
    transportId: number,
    isNewTransport: boolean,
    addedTrajectoryIds: number[],
    removedTrajectoryIds: number[],
    startDate: number,
    transportVehicleId: number,
    transportVehicleCapacity: number,
    chauffeurId: string,
    actionSequence?: TransportActionSequenceItem[],
    baseActionIds?: number[]
) => {
    return await apiHttpClient().put(ENDPOINT_UPDATE_ASSIGNMENTS, {
        transportId: transportId,
        isNewTransport: isNewTransport,
        addedTrajectoryIds: addedTrajectoryIds,
        removedTrajectoryIds: removedTrajectoryIds,
        startDate: startDate,
        transportVehicleId: transportVehicleId,
        transportVehicleCapacity: transportVehicleCapacity,
        chauffeurId: chauffeurId,
        actionSequence: actionSequence ?? null,
        baseActionIds: baseActionIds ?? null
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export interface TransportActionSequenceItem {
    transportActionType: TransportActionType;
    trajectoryId?: number | null;
}

export const getDestinationsByAssignmentId = async (assignmentId: number) => {
    return await apiHttpClient().post<AssignmentDestination[]>(ENDPOINT_GET_DESTINATIONS_BY_ASSIGNMENT_ID, { assignmentId })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const updateTrajectoryDestinations = async (transportTrajectoryDestinations: TransportTrajectoryDestinations) => {
    return await apiHttpClient().post(ENDPOINT_UPDATE_TRAJECTORY_DESTINATIONS, transportTrajectoryDestinations)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getMapTrajectories = async (request: MapTrajectoriesRequest) => {
    return await apiHttpClient().post<MapTrajectoryListModel[]>(ENDPOINT_GET_MAP_TRAJECTORIES, request)
        .then(response => response)
        .catch(error => error);
};

export interface OutsourceMailPayload {
    recipientEmail: string;
    recipientFullName: string;
    language: number;
}

export const outsourceTrajectories = async (trajectoryIds: number[], relationId: number, mail?: OutsourceMailPayload) => {
    return await apiHttpClient().post(ENDPOINT_OUTSOURCE_TRAJECTORIES, {
        trajectoryIds: trajectoryIds,
        relationId: relationId,
        recipientEmail: mail?.recipientEmail,
        recipientFullName: mail?.recipientFullName,
        language: mail?.language})
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
};

export const withdrawOutsourcedTrajectories = async (trajectoryIds: number[]) => {
    return await apiHttpClient().put(ENDPOINT_WITHDRAW_OUTSOURCED_TRAJECTORIES, {
        trajectoryIds: trajectoryIds
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const setTrajectoryStatus = async (
    trajectoryId: number,
    newTrajectoryActionStatus: TrajectoryActionStatus,
    emptyRideStatus: TrajectoryEmptyRideStatus | null,
) => {
    return await apiHttpClient().post(ENDPOINT_SET_TRAJECTORY_STATUS, {
        trajectoryId,
        newTrajectoryActionStatus,
        emptyRideStatus,
    });
};

export const revertTrajectoryToPlanned = async (trajectoryId: number) => {
    return await apiHttpClient().post(ENDPOINT_REVERT_TRAJECTORY_TO_PLANNED, {
        trajectoryId,
    });
};

export const revertEmptyRide = async (trajectoryId: number) => {
    return await apiHttpClient().post(ENDPOINT_REVERT_EMPTY_RIDE, {
        trajectoryId,
    });
};

export const setOutsourceStatus = async (
    trajectoryIds: number[],
    newOutsourceStatus: OutSourceStatus,
    emptyRideStatus: TrajectoryEmptyRideStatus | null = null,
) => {
    return await apiHttpClient().post(ENDPOINT_SET_OUTSOURCE_STATUS, {
        trajectoryIds,
        newOutsourceStatus,
        emptyRideStatus,
    });
};

export const addTrajectoryTag = async (trajectoryIds: number[], tag: string) => {
    return await apiHttpClient().post(ENDPOINT_ADD_TRAJECTORY_TAG, {
        trajectoryIds,
        tag,
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const removeTrajectoryTag = async (trajectoryId: number, tag: string) => {
    return await apiHttpClient().post(ENDPOINT_REMOVE_TRAJECTORY_TAG, {
        trajectoryId,
        tag,
    })
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}

export const getTrajectoryTagSummary = async () => {
    return await apiHttpClient().get<TrajectoryTagSummary>(ENDPOINT_TRAJECTORY_TAG_SUMMARY)
        .then(response => {
            return response;
        })
        .catch(error => {
            return error;
        })
}
